import * as React from "react";
import { useEffect, useState } from "react";
import classNames from "classnames";
import { Attribute } from "./Attributes";
import { FeatureInfo } from "./FeatureInfo";
import { getRelatedImagesData, GalleryImageWithId } from "../Gallery/imageProvider";
import { Feature } from "ol";
import { useMapContext } from "../../Contexts/MapContext";
import { useAppContext } from "../../Contexts/AppContext";
import { IdentifyResult, isFeatureValid, replaceWithValue } from "./identifyUtils";
import "./ResultFeatureInfo.scss";

interface ResultFeatureInfoProps {
    identified: IdentifyResult;
}

export const ResultFeatureInfo = ({ identified }: ResultFeatureInfoProps): JSX.Element => {
    const [images, setImages] = useState<GalleryImageWithId[]>([]);
    const { highlightFeatures, setHighlightFeatures } = useAppContext();
    const { configA } = useMapContext();

    const identifyIgnoreFields: string[] = configA.identifyIgnoreFields.map((f: string) => f.toLowerCase()) || [];

    const curLayer = configA.layers.find((conf) => conf.id === identified.mapLayerId);

    const onSelect = () => setHighlightFeatures([identified.feature]);

    useEffect(() => {
        if (!identified.feature || !configA.apiConfig || !curLayer || !curLayer.imageConfig) {
            return;
        }

        getRelatedImagesData(getImageProps(identified, configA.apiConfig, curLayer.imageConfig)).then(
            (imagesData: GalleryImageWithId[]) => imagesData && imagesData.length && setImages(imagesData),
            () => console.log("Objektam nav attēlu")
        );
    }, [identified.feature]);

    const selected =
        highlightFeatures &&
        highlightFeatures.features &&
        highlightFeatures.features.find((hl) => hl.get("OBJECTID") === identified.feature.get("OBJECTID"));

    return (
        <div className={classNames("collapsable-content", { selected })} onClick={onSelect}>
            <FeatureInfo
                attributes={getFeatureAttributes(
                    identified.layerId,
                    identified.feature,
                    identifyIgnoreFields,
                    curLayer?.identify
                )}
                images={images}
            />
        </div>
    );
};

interface ILink {
    attribute: string;
    link: string;
    target?: string;
    text?: string;
}

const getFeatureAttributes = (
    layerId: number,
    feature: Feature,
    identifyIgnoreFields: string[],
    customFields?: any
): Attribute[] => {
    let excludeFields = [];
    let linkFields = [] as ILink[];
    if (customFields && customFields[layerId]) {
        if (customFields[layerId].ignoreFields) {
            excludeFields = customFields[layerId].ignoreFields;
        }
        if (customFields[layerId].url) {
            linkFields = customFields[layerId].url;
        }
    }
    const exclude = identifyIgnoreFields.concat(excludeFields);
    const featureList = {};
    const attributeArray: Attribute[] = [];
    let attributes = feature.getProperties();
    Object.entries(attributes).forEach(([key, value]) => {
        const isLinkField = linkFields && linkFields.length && linkFields.find((l) => l.attribute === key);
        if (isFeatureValid(attributes, exclude, key, !!isLinkField)) {
            if (key.includes(":")) {
                const newAttribute = key.replace(":", "");
                delete Object.assign(featureList, attributes, {
                    [newAttribute]: attributes[key]
                })[key];
                key = newAttribute;
            }
            const thisValue = { label: key, value: value as string } as Attribute;

            if (linkFields && linkFields.length > 0) {
                for (const link of linkFields) {
                    if (link.attribute === key) {
                        thisValue.target = link.target ? link.target : "_new";
                        const label = link.link === "{0}" ? (value as string) : replaceWithValue(feature, link.link);
                        thisValue.url = label;
                        link.text && (thisValue.value = link.text);
                    }
                }
            }
            attributeArray.push(thisValue);
        }
    });
    if (!isEmpty(featureList)) {
        attributes = featureList;
    }

    return attributeArray;
};

function isEmpty(obj: any) {
    for (const key in obj) {
        if ({}.hasOwnProperty.call(obj, key)) {
            return false;
        }
    }
    return true;
}

const getImageProps = (res, apiConfig, imageConfig) => {
    const imagesConfig = imageConfig.find((curConfig) => curConfig.id === res.layerId);
    if (!imagesConfig || !imagesConfig.apiProviderId) {
        return;
    }

    const fileApiConfig = apiConfig.find((api) => api.id === imagesConfig.apiProviderId);

    return (
        imagesConfig &&
        fileApiConfig && {
            objectId: res.feature.get("OBJECTID"),
            fileApiConfig: fileApiConfig,
            imagesConfig: imagesConfig
        }
    );
};
